import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";

type Proposal = {
	id: string;
	negotiationId: string;
	auctionId: string | null;
	fromSide: "brand" | "affiliate";
	fromAgentId: string;
	bodyJson: string;
	state: string;
	approvalTtlAt: number | null;
	approvalExtensions: number;
	createdAt: number;
};

type Resp = { items: Proposal[]; total: number };

export function Approvals() {
	const qc = useQueryClient();
	const { data, isLoading } = useQuery<Resp>({
		queryKey: ["proposals", "pending-approval"],
		queryFn: () => api.get("/proposals/pending-approval"),
		refetchInterval: 30_000,
	});

	const approveMut = useMutation({
		mutationFn: (id: string) => api.post(`/proposals/${id}/approve`, {}),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["proposals", "pending-approval"] }),
	});
	const rejectMut = useMutation({
		mutationFn: ({ id, reason }: { id: string; reason: string }) =>
			api.post(`/proposals/${id}/reject`, { reason }),
		onSuccess: () => qc.invalidateQueries({ queryKey: ["proposals", "pending-approval"] }),
	});

	if (isLoading) return <div className="p-6">Loading…</div>;
	const items = data?.items ?? [];

	return (
		<div className="space-y-4 p-6">
			<h1 className="text-xl font-semibold">Pending approvals ({items.length})</h1>
			<p className="text-sm text-neutral-600">
				Every outbound proposal drafted by your agent waits here. Approve → send. Reject → cancel.
			</p>
			{items.length === 0 && <div className="text-neutral-500">Nothing pending.</div>}
			{items.map((p) => (
				<ApprovalCard
					key={p.id}
					proposal={p}
					onApprove={() => approveMut.mutate(p.id)}
					onReject={(reason) => rejectMut.mutate({ id: p.id, reason })}
				/>
			))}
		</div>
	);
}

function ApprovalCard(props: {
	proposal: Proposal;
	onApprove: () => void;
	onReject: (reason: string) => void;
}) {
	const [rejecting, setRejecting] = useState(false);
	const [reason, setReason] = useState("");
	const ttlLeft = props.proposal.approvalTtlAt
		? Math.max(0, props.proposal.approvalTtlAt * 1000 - Date.now())
		: null;
	return (
		<div className="rounded border border-neutral-200 bg-white p-4 shadow-sm">
			<div className="flex items-center justify-between text-xs text-neutral-500">
				<div>
					Negotiation <span className="font-mono">{props.proposal.negotiationId}</span> · from{" "}
					{props.proposal.fromSide}
				</div>
				<div>
					{ttlLeft != null && `TTL ${Math.round(ttlLeft / 1000 / 60)}m`} · extensions{" "}
					{props.proposal.approvalExtensions}/2
				</div>
			</div>
			<pre className="mt-2 max-h-48 overflow-auto rounded bg-neutral-50 p-2 text-xs">
				{prettyJson(props.proposal.bodyJson)}
			</pre>
			<div className="mt-3 flex gap-2">
				<button
					type="button"
					onClick={props.onApprove}
					className="rounded bg-emerald-600 px-3 py-1 text-sm text-white hover:bg-emerald-700"
				>
					Approve & send
				</button>
				{!rejecting ? (
					<button
						type="button"
						onClick={() => setRejecting(true)}
						className="rounded bg-neutral-200 px-3 py-1 text-sm hover:bg-neutral-300"
					>
						Reject
					</button>
				) : (
					<div className="flex flex-1 items-center gap-2">
						<input
							placeholder="Reason"
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							className="flex-1 rounded border px-2 py-1 text-sm"
						/>
						<button
							type="button"
							disabled={!reason.trim()}
							onClick={() => props.onReject(reason.trim())}
							className="rounded bg-red-600 px-3 py-1 text-sm text-white disabled:opacity-50"
						>
							Confirm reject
						</button>
						<button
							type="button"
							onClick={() => setRejecting(false)}
							className="text-sm text-neutral-600 underline"
						>
							Cancel
						</button>
					</div>
				)}
			</div>
		</div>
	);
}

function prettyJson(s: string): string {
	try {
		return JSON.stringify(JSON.parse(s), null, 2);
	} catch {
		return s;
	}
}
